import { QWSService } from '../types/algorithm';

export const QWS_PROPERTY_LABELS = [
  'Response Time',
  'Availability',
  'Throughput',
  'Successability',
  'Reliability',
  'Compliance',
  'Best Practices',
  'Latency',
  'Documentation',
] as const;

export interface QWSMatrixPreviewRow {
  serviceIndex: number;
  serviceName: string;
  wsdlUrl: string;
  values: number[];
}

/**
 * Safely parses a numeric value from CSV, returning NaN for missing/invalid data
 * CRITICAL: Returns NaN (not 0) for missing data to avoid injecting fake zeros.
 * Missing data should be ignored in min/max computation and aggregation.
 */
function parseNumericValue(value: string): number {
  const x = parseFloat(value);
  return Number.isFinite(x) ? x : NaN;
}

/**
 * Parses QWS dataset from CSV text format
 * Expected format: ResponseTime, Availability, Throughput, Successability, 
 * Reliability, Compliance, BestPractices, Latency, Documentation, ServiceName, WSDL_URL
 * 
 * CRITICAL FIX: Missing/invalid numeric fields are now parsed as NaN (not 0).
 * This prevents fake zeros from corrupting min/max ranges, normalization, and energy/cost calculations.
 */
export function parseQWSDataset(csvText: string): QWSService[] {
  const lines = csvText.trim().split('\n');
  const services: QWSService[] = [];

  for (const line of lines) {
    if (!line.trim()) continue;

    const parts = line.split(',').map(p => p.trim());
    if (parts.length < 9) continue;

    try {
      const service: QWSService = {
        responseTime: parseNumericValue(parts[0]),
        availability: parseNumericValue(parts[1]),
        throughput: parseNumericValue(parts[2]),
        successability: parseNumericValue(parts[3]),
        reliability: parseNumericValue(parts[4]),
        compliance: parseNumericValue(parts[5]),
        bestPractices: parseNumericValue(parts[6]),
        latency: parseNumericValue(parts[7]),
        documentation: parseNumericValue(parts[8]),
        serviceName: parts[9] || '',
        wsdlUrl: parts[10] || '',
      };

      services.push(service);
    } catch (error) {
      console.warn('Failed to parse line:', line, error);
    }
  }

  return services;
}

/**
 * Normalizes the matrix seed into a stable positive integer for deterministic previews and runs.
 */
export function resolveMatrixSeed(seed?: number): number {
  let actualSeed = seed ?? Math.floor(Date.now() % 2147483647);
  actualSeed = Math.abs(Math.trunc(actualSeed)) % 2147483647;

  if (actualSeed === 0) {
    actualSeed = 1;
  }

  return actualSeed;
}

function getServiceQoSValues(service: QWSService): number[] {
  return [
    service.responseTime,
    service.availability,
    service.throughput,
    service.successability,
    service.reliability,
    service.compliance,
    service.bestPractices,
    service.latency,
    service.documentation,
  ];
}

function getTaskQoSValues(
  baseValues: number[],
  task: number,
  serviceIdx: number,
  seed: number,
  taskSpecificVariationEnabled: boolean
): number[] {
  if (!taskSpecificVariationEnabled) {
    return [...baseValues];
  }

  return baseValues.map((value, propertyIdx) =>
    applyTaskVariation(value, task, serviceIdx, propertyIdx, seed)
  );
}

/**
 * Simple seeded random number generator for deterministic task-specific variation
 * Uses a linear congruential generator (LCG) for reproducibility
 */
function seededRandom(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

/**
 * Applies task-specific variation to a QoS value
 * Uses deterministic variation based on global seed + task/service/property indices for reproducibility
 * Variation range: ±5% to simulate real-world task-specific performance differences
 * 
 * CRITICAL: Preserves NaN values (missing data) - do not apply variation to NaN
 * 
 * @param baseValue The base QoS value from the dataset
 * @param task Task index
 * @param serviceIdx Service index
 * @param propertyIdx Property index
 * @param globalSeed Global seed for reproducibility (combined with indices for deterministic variation)
 */
function applyTaskVariation(
  baseValue: number,
  task: number,
  serviceIdx: number,
  propertyIdx: number,
  globalSeed: number
): number {
  // Preserve NaN (missing data) - do not apply variation
  if (!Number.isFinite(baseValue)) return baseValue;
  
  // Skip variation for zero values to avoid division issues
  if (baseValue === 0) return baseValue;
  
  // Create a deterministic seed from global seed + task, service, and property indices
  // This ensures reproducibility: same global seed + same indices = same variation
  const combinedSeed = ((globalSeed * 7919 + task * 1000000 + serviceIdx * 1000 + propertyIdx) % 2147483647);
  const random = seededRandom(combinedSeed);
  
  // Apply ±5% variation (deterministic but task-specific)
  const variation = (random() - 0.5) * 0.1; // Range: [-0.05, +0.05]
  const variedValue = baseValue * (1 + variation);
  
  // Ensure non-negative values (some properties like responseTime, latency must be >= 0)
  return Math.max(0, variedValue);
}

/**
 * Builds quality matrix from QWS services and task count
 * Creates a 3D array: matrix[task][service][property]
 * This avoids Map size limits for large datasets
 * 
 * CRITICAL FIX: Each task now gets task-specific QoS variations to prevent trivial solutions.
 * Without this, all tasks would have identical service options, making the optimal solution
 * trivial (just pick the same best service for all tasks). Task-specific variation simulates
 * real-world scenarios where the same service may perform differently for different tasks.
 * 
 * THESIS REPRODUCIBILITY: Uses a seeded RNG for deterministic task-specific variation.
 * The same seed will produce identical results across runs, ensuring reproducibility for
 * thesis experiments. The seed is logged for documentation purposes.
 * 
 * @param services Array of QWS services from the dataset
 * @param taskCount Number of tasks
 * @param seed Optional seed for RNG. If not provided, generates a deterministic seed based on current time.
 *             For thesis experiments, provide a fixed seed and log it for reproducibility.
 * @returns Object containing the quality matrix and the seed used
 */
export function buildQualityMatrix(
  services: QWSService[],
  taskCount: number,
  seed?: number,
  taskSpecificVariationEnabled: boolean = true
): { matrix: number[][][]; seed: number } {
  const actualSeed = resolveMatrixSeed(seed);
  
  // Log seed for reproducibility (important for thesis experiments)
  console.log(
    `[QWS Matrix Builder] Using seed: ${actualSeed} with task-specific variation ${taskSpecificVariationEnabled ? 'enabled' : 'disabled'}`
  );
  
  // 3D array: [task][service][property]
  const matrix: number[][][] = [];

  for (let task = 0; task < taskCount; task++) {
    matrix[task] = [];
    for (let serviceIdx = 0; serviceIdx < services.length; serviceIdx++) {
      const baseValues = getServiceQoSValues(services[serviceIdx]);

      matrix[task][serviceIdx] = getTaskQoSValues(
        baseValues,
        task,
        serviceIdx,
        actualSeed,
        taskSpecificVariationEnabled
      );
    }
  }

  return { matrix, seed: actualSeed };
}

/**
 * Builds a single task slice of the QoS matrix for previewing in the UI.
 * This avoids allocating the entire 3D matrix when the user only needs to inspect one task.
 */
export function buildQualityMatrixTaskSlice(
  services: QWSService[],
  taskIndex: number,
  seed?: number,
  taskSpecificVariationEnabled: boolean = true
): { rows: QWSMatrixPreviewRow[]; seed: number } {
  const actualSeed = resolveMatrixSeed(seed);
  const safeTaskIndex = Math.max(0, Math.trunc(taskIndex));

  const rows = services.map((service, serviceIdx) => ({
    serviceIndex: serviceIdx,
    serviceName: service.serviceName || `Service ${serviceIdx + 1}`,
    wsdlUrl: service.wsdlUrl,
    values: getTaskQoSValues(
      getServiceQoSValues(service),
      safeTaskIndex,
      serviceIdx,
      actualSeed,
      taskSpecificVariationEnabled
    ),
  }));

  return { rows, seed: actualSeed };
}
